/**
 * Centralized DataDog event names for authentication flow
 *
 * Event Naming Convention: auth.{component}.{action}
 * - Consistent dot-notation hierarchy
 * - Easy to query in DataDog dashboards
 * - Organized by functional area
 *
 * Usage:
 *   import { DD_AUTH_EVENTS, logAuthEvent } from './datadogEvents';
 *   logAuthEvent(DD_AUTH_EVENTS.CIRCUIT_OPENED, { name: 'TokenRefresh', failures: 5 });
 */

import { serverLogger } from '../libs/datadog-logger/loggers/serverLogger';

export const DD_AUTH_EVENTS = {
  // Circuit Breaker Events
  CIRCUIT_OPENED: 'auth.circuit.opened',
  CIRCUIT_CLOSED: 'auth.circuit.closed',
  CIRCUIT_REJECTED: 'auth.circuit.rejected',
  CIRCUIT_HALF_OPEN: 'auth.circuit.half_open',

  // Token Refresh Events
  REFRESH_SUCCESS: 'auth.refresh.success',
  REFRESH_FAILED: 'auth.refresh.failed',
  REFRESH_TIMEOUT: 'auth.refresh.timeout',
  REFRESH_SINGLE_FLIGHT: 'auth.refresh.single_flight',

  // Validation Events
  VALIDATION_ERROR: 'auth.validation.error',
  VALIDATION_SUCCESS: 'auth.validation.success',

  // Cross-Tab Events
  CROSS_TAB_SYNC: 'auth.cross_tab.sync',
  CROSS_TAB_TIMEOUT: 'auth.cross_tab.timeout',

  // Token Expiration Events (for URL-based auth)
  TOKEN_EXPIRED: 'auth.token.expired',
  TOKEN_INVALID: 'auth.token.invalid',
  TOKEN_MISSING: 'auth.token.missing',
};

/**
 * MOP (Method of Payment) Authorization Events
 * For tracking user journey through MOP authorization flow
 */
export const DD_MOP_EVENTS = {
  // Page/Component Lifecycle
  PAGE_VIEW: 'mop.page_view',
  COMPONENT_MOUNT: 'mop.component_mount',

  // Authentication Status
  AUTH_SUCCESS: 'mop.auth.success',
  AUTH_FAILED: 'mop.auth.failed',
  AUTH_EXPIRED: 'mop.auth.expired',
  AUTH_INVALID: 'mop.auth.invalid',
  AUTH_MISSING: 'mop.auth.missing',

  // Error Screens Displayed
  ERROR_SCREEN_EXPIRED: 'mop.error_screen.expired',
  ERROR_SCREEN_INVALID: 'mop.error_screen.invalid',
  ERROR_SCREEN_MISSING: 'mop.error_screen.missing',
  ERROR_SCREEN_NETWORK: 'mop.error_screen.network',
  ERROR_SCREEN_DATA_LOAD: 'mop.error_screen.data_load',

  // ZIP Verification Flow
  ZIP_SUBMIT_START: 'mop.zip.submit_start',
  ZIP_VALIDATION_FAILED: 'mop.zip.validation_failed',
  ZIP_API_START: 'mop.zip.api_start',
  ZIP_SUCCESS: 'mop.zip.success',
  ZIP_DENIED: 'mop.zip.denied',
  ZIP_ERROR: 'mop.zip.error',

  // Redirect Events
  REDIRECT_INTERNAL: 'mop.redirect.internal',
  REDIRECT_EXTERNAL: 'mop.redirect.external',
};

/**
 * Payment Application Events
 * For tracking user journey through payment application flow
 */
export const DD_PAYMENT_APP_EVENTS = {
  // Page/Component Lifecycle
  PAGE_VIEW: 'payment_app.page_view',
  COMPONENT_MOUNT: 'payment_app.component_mount',
  FORM_READY: 'payment_app.form_ready',

  // Authentication Status
  AUTH_SUCCESS: 'payment_app.auth.success',
  AUTH_FAILED: 'payment_app.auth.failed',

  // Data Loading
  DATA_LOAD_START: 'payment_app.data.load_start',
  DATA_LOAD_STEP: 'payment_app.data.load_step',
  DATA_LOAD_SUCCESS: 'payment_app.data.load_success',
  DATA_LOAD_FAILED: 'payment_app.data.load_failed',

  // Error Screens
  ERROR_SCREEN_AUTH: 'payment_app.error_screen.auth',
  ERROR_SCREEN_NETWORK: 'payment_app.error_screen.network',
  ERROR_SCREEN_DATA_LOAD: 'payment_app.error_screen.data_load',

  // Decisions (LESSEEPORT-1618)
  PAGE_OPENED: 'payment_app.page.opened',
  DIRECT_DEBIT_REQUIRED: 'payment_app.direct_debit.required',
  METHOD_OF_PAYMENT_SELECTED: 'payment_app.method_of_payment.selected',

  // Payment Method Selection
  PAYMENT_METHOD_SELECTED: 'payment_app.payment_method.selected',
  ADD_PAYMENT_METHOD_CLICKED: 'payment_app.payment_method.add_clicked',
  PAYMENT_METHOD_ADDED: 'payment_app.payment_method.added',
  PAYMENT_METHOD_ADDED_NOT_FOUND: 'payment_app.payment_method.added_not_found',

  // Form Submission
  SUBMIT_CLICKED: 'payment_app.submit.clicked',
  VALIDATION_FAILED: 'payment_app.validation.failed',

  // Document Generation
  DOCUMENT_GEN_START: 'payment_app.document.gen_start',
  DOCUMENT_GEN_SUCCESS: 'payment_app.document.gen_success',
  DOCUMENT_GEN_FAILED: 'payment_app.document.gen_failed',
  DOCUMENT_SIGN_REJECTED: 'payment_app.document.sign_rejected',
  DOCUSIGN_REDIRECT: 'payment_app.docusign.redirect',

  // MOP Progress (for MOP flow completion)
  MOP_PROGRESS_START: 'payment_app.mop_progress.start',
  MOP_PROGRESS_SUCCESS: 'payment_app.mop_progress.success',
  MOP_PROGRESS_FAILED: 'payment_app.mop_progress.failed',
};

/**
 * Helper to safely log DataDog events
 *
 * @param {string} eventName - Event name from DD_AUTH_EVENTS
 * @param {object} context - Additional context data (optional)
 *
 * @example
 * logAuthEvent(DD_AUTH_EVENTS.CIRCUIT_OPENED, {
 *   name: 'TokenRefresh',
 *   failures: 5,
 *   threshold: 5,
 *   timeoutMs: 60000
 * });
 */
export function logAuthEvent(eventName, context = {}) {
  try {
    serverLogger.info(eventName, context);
  } catch (error) {
    // Silently fail if DataDog is not available or errors
    // Don't let DataDog logging break auth flow
    console.debug('[DataDog] Failed to log event:', eventName, error);
  }
}

/**
 * Backward Compatibility Map
 * Maps old event names to new standardized names
 * Can be used to maintain existing dashboards while migrating
 */
export const DD_LEGACY_EVENT_MAP = {
  'auth.circuit_breaker_opened': DD_AUTH_EVENTS.CIRCUIT_OPENED,
  'auth.circuit_breaker_closed': DD_AUTH_EVENTS.CIRCUIT_CLOSED,
  'auth.circuit_breaker_rejected': DD_AUTH_EVENTS.CIRCUIT_REJECTED,
};
